import React, { useState, useEffect, useRef } from 'react';
import { Input, notification, Modal, InputRef } from 'antd';
import { Button, Title } from '@cognite/cogs.js';
import styled from 'styled-components/macro';
import { useUrl } from 'hooks/useUrls';
import mixpanel from 'utils/mixpanel-frontend';
import { getDigitalTwinUrl } from 'utils/router-url';
import {
  useGetDigitalTwinsQuery,
  useCreateDigitalTwinMutation,
} from 'types/dt-graphql-apollo-v2';
import { useStatefulHistory } from 'hooks/history';
import { v1 as uuidv1 } from 'uuid';

export const NewDigitalTwin = ({ onCancel }: { onCancel: VoidFunction }) => {
  const history = useStatefulHistory();
  const tenant = useUrl.Tenant();
  const [createDigitalTwin] = useCreateDigitalTwinMutation();
  const [digitalTwinName, setDigitalTwintName] = useState('');
  const [creating, setCreating] = useState(false);

  const inputRef = useRef<InputRef>(null);

  const { refetch: refetchDigitalTwins } = useGetDigitalTwinsQuery();

  useEffect(() => {
    inputRef?.current?.focus();
  }, []);

  const onChangeDigitalTwinName = (e: React.FormEvent<HTMLInputElement>) => {
    setDigitalTwintName(e.currentTarget.value);
  };

  const onEnterPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onCreate();
    }
  };

  const onCreate = async () => {
    const name = digitalTwinName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const digitalTwin = await createDigitalTwin({
        variables: {
          id: uuidv1(),
          name,
          tenant,
        },
      });
      mixpanel.track('Created project');
      refetchDigitalTwins();
      history.pushStateful(
        `${getDigitalTwinUrl(
          tenant,
          digitalTwin.data?.Create_DigitalTwin?.externalId!
        )}/schemas`
      );
    } catch (err: any) {
      notification.error({
        message: 'Could not create project',
        description: err.toString(),
      });
    } finally {
      setCreating(false);
      setDigitalTwintName('');
      onCancel();
    }
  };

  return (
    <Modal
      visible
      maskTransitionName=""
      title={null}
      onCancel={() => {
        onCancel();
      }}
      footer={null}
    >
      <StyledContainer>
        <StyledRowContainer>
          <StyledColumnContainer>
            <StyledCardTitle level={4}>
              Get started with your
              <br />
              project
            </StyledCardTitle>
            <StyledDescription>
              Manage, control and monitor your project data. Get started by
              creating a project and add your project schemas.
            </StyledDescription>
          </StyledColumnContainer>
          <StyledEllipse>
            <StyledImage
              src={`${process.env.PUBLIC_URL}/images/workflow/workflow-step-3.svg`}
              alt="Create a new project"
            />
          </StyledEllipse>
        </StyledRowContainer>
        <StyledLabel>Project name</StyledLabel>
        <StyledInput
          ref={inputRef}
          placeholder="Name your project"
          title="Project name"
          value={digitalTwinName}
          onChange={onChangeDigitalTwinName}
          onKeyDown={onEnterPress}
        />
        <StyledButtons>
          <Button style={{ marginRight: '0.5rem' }} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="primary"
            disabled={!digitalTwinName.trim()}
            onClick={onCreate}
            loading={creating}
          >
            Create project
          </Button>
        </StyledButtons>
      </StyledContainer>
    </Modal>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 0.5rem;
  border-radius: 8px;
`;

const StyledCardTitle = styled(Title)`
  font-weight: 500;
  font-size: 24px;
  line-height: 32px;
  margin-top: 2rem;
`;

const StyledImage = styled.img`
  align-self: center;
  flex: 1;
  height: 70%;
`;

const StyledEllipse = styled.div`
  display: flex;
  align-items: center;
  width: 140px;
  height: 140px;
  border-radius: 50%;
  margin-top: 3rem;
  box-shadow: 0px 6px 30px rgba(74, 103, 251, 0.25);
`;

const StyledInput = styled(Input)`
  width: 226px;
  height: 40px;
  border: 1px solid #404040;
  border-radius: 4px;
  font-size: 16px;
  margin-bottom: 2rem;
`;

const StyledLabel = styled.label`
  font-family: Inter;
  font-style: normal;
  font-size: 16px;
  line-height: 20px;
  margin: 4px 0px;
`;

const StyledRowContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const StyledColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledButtons = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
`;

const StyledDescription = styled.p`
  margin: 0.5rem 0 2rem 0;
  font-size: 12px;
  width: 256px;
`;
